/**
 * Utility functions for managing navigation state between modals and pages
 * Uses localStorage with UUID keys passed as query parameters
 */

const STATE_KEY_PREFIX = 'shiftNavState_'
const STATE_EXPIRY_MS = 5 * 60 * 1000 // 5 minutes
const QUERY_PARAM_NAME = 'state'

interface StoredState<T = any> {
  data: T
  timestamp: number
}

/**
 * Generate a unique ID for state storage
 */
export const generateStateId = (): string => {
  // Use crypto.randomUUID if available, fallback to timestamp + random
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Store navigation state and return the state ID
 */
export const storeNavigationState = <T>(data: T): string => {
  const stateId = generateStateId()
  const storageKey = `${STATE_KEY_PREFIX}${stateId}`

  const storedState: StoredState<T> = {
    data,
    timestamp: Date.now(),
  }

  try {
    localStorage.setItem(storageKey, JSON.stringify(storedState))
  } catch {}

  return stateId
}

/**
 * Retrieve navigation state using state ID from query params
 */
export const retrieveNavigationState = <T>(stateId?: string | null): T | undefined => {
  if (!stateId) return undefined

  const storageKey = `${STATE_KEY_PREFIX}${stateId}`

  try {
    const stored = localStorage.getItem(storageKey)
    if (!stored) return undefined

    const storedState: StoredState<T> = JSON.parse(stored)

    // Check if state has expired
    const now = Date.now()
    const isExpired = now - storedState.timestamp > STATE_EXPIRY_MS

    if (isExpired) {
      localStorage.removeItem(storageKey)
      return undefined
    }

    return storedState.data
  } catch {
    // Clean up corrupted data
    try {
      localStorage.removeItem(storageKey)
    } catch {}
    return undefined
  }
}

/**
 * Clean up navigation state from localStorage
 * Should be called after all components have retrieved the state they need
 */
export const cleanNavigationState = (stateId?: string | null): void => {
  if (!stateId) return

  const storageKey = `${STATE_KEY_PREFIX}${stateId}`

  try {
    localStorage.removeItem(storageKey)
  } catch {}
}

/**
 * Remove state parameter from URL without triggering navigation
 * This cleans up the URL after the state has been consumed
 */
export const cleanStateFromUrl = (): void => {
  const url = new URL(window.location.href)
  const params = new URLSearchParams(url.search)

  if (params.has(QUERY_PARAM_NAME)) {
    params.delete(QUERY_PARAM_NAME)

    // Rebuild the URL without the state parameter
    const newSearch = params.toString()
    const newUrl = `${url.pathname}${newSearch ? `?${newSearch}` : ''}${url.hash}`

    // Replace the current history entry without triggering navigation
    window.history.replaceState(window.history.state, '', newUrl)
  }
}

/**
 * Get state ID from current URL query parameters
 */
export const getStateIdFromUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search)
  return params.get(QUERY_PARAM_NAME)
}

/**
 * Build URL with state ID as query parameter
 */
export const buildUrlWithState = (baseUrl: string, stateId: string): string => {
  const separator = baseUrl.includes('?') ? '&' : '?'
  return `${baseUrl}${separator}${QUERY_PARAM_NAME}=${stateId}`
}

/**
 * Clean up expired navigation states
 * Can be called on app initialization
 */
export const cleanupExpiredStates = (): void => {
  const now = Date.now()
  const keysToRemove: string[] = []

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STATE_KEY_PREFIX)) {
        try {
          const stored = localStorage.getItem(key)
          if (stored) {
            const storedState: StoredState = JSON.parse(stored)
            if (now - storedState.timestamp > STATE_EXPIRY_MS) {
              keysToRemove.push(key)
            }
          }
        } catch {
          // Remove corrupted entries
          keysToRemove.push(key)
        }
      }
    }

    // Remove expired/corrupted entries
    keysToRemove.forEach((key) => {
      try {
        localStorage.removeItem(key)
      } catch {}
    })
  } catch {}
}
